import Image from "../../lib/Image";
import { NavLink } from "../../lib/Navigation";
import { useEffect, useMemo, useState } from "react";
import { useUpdate } from "ahooks";
import general from "../../general";
import useActiveState from "../../lib/useActiveState";
import dayjs from "dayjs";
import { storageLocal, sessionLocal } from "../util/storage";

export default (props) => {
  const update = useUpdate();
  const [app] = useActiveState("app");

  // 悬浮球数据：只要后端有数据就一直在
  const [datas, setDatas] = useState(null);

  // 大弹窗数据：自动弹（受控）/ 手动弹（不受控）
  const [data, setData] = useState(null);

  // 用户关闭/点击跳转后，本次进入不再自动弹（但手动仍可弹）
  const [alerted, setAlerted] = useState(false);

  // ===== keys =====
  const KEYS = useMemo(
    () => ({
      DAILY_AUTO: "act_popup_auto_daily_by_id", // 大弹窗自动弹，每个id每天最多3次 storageLocal
      AUTO_SHOWN: "act_popup_auto_shown_this_session_by_id", // 本次进入程序，只自动弹一次；防止：iOS下拉刷新重复自动弹，页面重渲染重复自动弹  sessionLocal
      FETCHED: "act_popup_fetched_this_session", // 本次 session 只请求一次接口，防止：页面来回跳转一直请求，active 反复变化一直请求 sessionLocal
      CACHE: "act_popup_cached_data", // 解决下拉刷新后悬浮球消失的问题，缓存上次活动数据 sessionLocal
      POPUPDATA: "popupData", // 跨页面传递活动数据 sessionLocal
    }),
    [],
  );

  const todayStr = dayjs().format("YYYY-MM-DD");
  const normId = (id) => String(id);

  // ========= storageLocal：每日自动弹次数（只限制自动弹） =========
  const getDailyAutoCount = (id) => {
    const key = normId(id);
    const raw = storageLocal.get(KEYS.DAILY_AUTO);
    if (!raw || raw.date !== todayStr || !raw.items) return 0;
    return raw.items[key] || 0;
  };

  const incDailyAutoCount = (id) => {
    const key = normId(id);
    const raw = storageLocal.get(KEYS.DAILY_AUTO);
    const items =
      raw && raw.date === todayStr && raw.items ? { ...raw.items } : {};
    items[key] = (items[key] || 0) + 1;
    storageLocal.set(KEYS.DAILY_AUTO, { date: todayStr, items });
  };

  const boxPopupLimitCount = sessionLocal.get("boxPopupLimitCount") || 2;
  const canAutoShowToday = (id) => getDailyAutoCount(id) < boxPopupLimitCount;

  // ========= sessionLocal：按 id 记 mark（auto shown / etc.） =========
  const getSessionMap = (storeKey) => sessionLocal.get(storeKey) || {};
  const hasSessionMark = (storeKey, id) =>
    !!getSessionMap(storeKey)?.[normId(id)];
  const setSessionMark = (storeKey, id) => {
    const map = { ...getSessionMap(storeKey), [normId(id)]: true };
    sessionLocal.set(storeKey, map);
  };

  // ========= 统一塞入悬浮球数据（不做次数限制） + 缓存 =========
  const applyActivity = (raw) => {
    if (!raw?.id) return;
    const normalized = { ...raw, route: raw?.jump_value ?? raw?.route };
    setDatas(normalized);
    sessionLocal.set(KEYS.CACHE, normalized);
  };

  // ========= 拉取数据：先恢复缓存/消费 popupData；本 session 只请求一次 =========
  useEffect(() => {
    if (!props.active) return;

    // 0) 先恢复缓存（下拉刷新后也能展示悬浮球）
    const cachedData = sessionLocal.get(KEYS.CACHE);
    if (cachedData?.id) setDatas(cachedData);
    // 不 return，继续往下走，但仍然有 FETCHED=true 兜底，只会请求一次

    // 1) popupData 优先（消费一次）
    const popupData = sessionLocal.get(KEYS.POPUPDATA);
    if (popupData) {
      sessionLocal.remove(KEYS.POPUPDATA);
      applyActivity(popupData);
      return;
    }

    // 2) 本 session 只请求一次（避免路由返回/active 反复触发）
    if (sessionLocal.get(KEYS.FETCHED) === true) return;
    sessionLocal.set(KEYS.FETCHED, true);

    general.axios.post("/v3/movable/popup/popup").then(({ data: resData }) => {
      const { status, data: apiData } = resData || {};
      if (status?.succeed && apiData) applyActivity(apiData);
    });
  }, [props.active, KEYS]); // KEYS 是 memo 的，不会抖

  // ========= 自动弹：standalone + 本 session 仅一次 + daily<=3 + 未alerted =========
  useEffect(() => {
    if (!window.navigator.standalone) {
      update();
      return;
    }

    const id = datas?.id;
    if (!id) {
      setData(null);
      update();
      return;
    }

    if (alerted) {
      setData(null);
      update();
      return;
    }

    if (hasSessionMark(KEYS.AUTO_SHOWN, id)) {
      setData(null);
      update();
      return;
    }

    if (!canAutoShowToday(id)) {
      setData(null);
      update();
      return;
    }

    // 满足条件：执行一次自动弹 + 记 session + 记 daily
    setSessionMark(KEYS.AUTO_SHOWN, id);
    incDailyAutoCount(id);
    setData(datas);

    update();
  }, [datas, alerted, KEYS]); // KEYS 不会变

  // ========= 手动点悬浮球：一直允许 =========
  const tips = (d) => {
    if (!d?.id) return;
    setData(d);
    spm(d, "tips");
  };

  const spm = (d, opt) => {
    general.axios.post("/v3/spm", {
      spm: `activity${d.id ? `.${d.id}` : ""}.${opt}`,
    });
  };

  const alertFunc = (d) => {
    setData(null);
    spm(d, "alert");
    setAlerted(true);
  };

  const close = (d) => {
    setData(null);
    spm(d, "close");
    setAlerted(true);
  };

  const canRenderBall = !!datas?.thumb_images;
  const canRenderPopup = !!data?.image;

  return (
    <>
      {/* 悬浮球：只要后端有数据就一直在（不受次数限制） */}
      {canRenderBall && (
        <div
          className={
            "act-datas " +
            (props.isShow === "open" && props.advertis?.img_url
              ? "act-datas-113"
              : storageLocal.has("kfUrl")
                ? "act-datas-130"
                : "act-datas-60")
          }
        >
          <div className="item" onClick={() => tips(datas)}>
            <Image
              className={app.brand}
              src={datas?.thumb_images}
              alt={datas?.title}
            />
          </div>
        </div>
      )}

      {/* 大弹窗：自动弹受控；手动弹不受控 */}
      {canRenderPopup && (
        <div className="act-data">
          <div className="container">
            <NavLink
              to={
                data.jump_type == 2
                  ? `${data.route}`
                  : `/game?mid=${data.route}`
              }
              iframe={
                data?.route?.startsWith("http")
                  ? { title: data.title }
                  : undefined
              }
              user={data.verify_login == 1 ? true : false}
              onClick={() => alertFunc(data)}
            >
              <div className="img">
                <Image src={data.image} alt="" />
              </div>
            </NavLink>
            <div className="close" onClick={() => close(data)}>
              X
            </div>
          </div>
        </div>
      )}
    </>
  );
};
